import { Attr, BelongsTo, Model } from "spraypaint";

import { type AIVisibilityProjectSource } from "shared/modules/AIVisibility/types";
import { ApplicationRecord } from "shared/services/spraypaint";
import type { Nullable } from "shared/types";

import type InstantInsightsReport from "./InstantInsightsReport";
import type Organisation from "./Organisation";
import type User from "./User";

export interface AIVisibilityProjectEntityConfig {
  description: string;
  duplicate_groups: Record<string, unknown>;
  exclusions: string[];
  inclusions: string[];
  name: string;
}

interface AIVisibilityProjectMeta {
  llm_entities_config: Record<string, AIVisibilityProjectEntityConfig>;
}

export interface AIVisibilityProjectRawRequest {
  brand_name: string;
  locations: { country_code: string }[];
  prompts: string[];
  sources: AIVisibilityProjectSource[];
}

interface AIVisibilityProjectRawResponse {
  auto_update: boolean;
  company_id: number;
  company_slug: string;
  created_by: string;
  creation_date: string;
  datastudio_url: string;
  description: Nullable<string>;
  id: number;
  image_url: string;
  is_active: boolean;
  is_shared: Nullable<boolean>;
  keywords_count: number;
  last_auto_update_date: string;
  last_updated_by: Nullable<string>;
  last_updated_date: string;
  locations_count: number;
  meta: AIVisibilityProjectMeta;
  permissions_max_countries: number;
  permissions_max_keywords: number;
  permissions_max_locations: number;
  permissions_stories: boolean;
  project_name: string;
  project_type: Nullable<string>;
  slug: string;
  state: string;
  workspace_id: number;
  workspace_slug: string;
}

export interface AIVisibilityProjectRaw {
  request: AIVisibilityProjectRawRequest;
  response: AIVisibilityProjectRawResponse;
}

export type AIVisibilityProjectHealth =
  | "ok"
  | "updating"
  | "degraded"
  | "credits_exhausted"
  | "failed"
  | "no_data"
  | "unknown";

export type AIVisibilityProjectStatus =
  | "available"
  | "project_initialization.failed"
  | "project_initialization.ongoing";

interface AIVisibilityProjectSelectedEntities {
  brands?: string[];
  products?: string[];
}

export type AIVisibilityProjectSetupState = "pending" | "ready" | "failed";

@Model()
class AIVisibilityProject extends ApplicationRecord {
  static apiNamespace = "/ai";

  static endpoint = "/visibility_projects";

  static jsonapiType = "ai_visibility_projects";

  // Owning Trajaan company. Read-only, and present so the company resource's
  // projects sideload can filter on it.
  @Attr({ persist: false }) aiVisibilityCompanyId: number;

  @Attr({ persist: false }) archived: boolean;

  @Attr({ persist: false }) archivedAt: string;

  @Attr({ persist: false }) createdAt: string;

  @BelongsTo() creator: User;

  @Attr() creatorId: number;

  @Attr({ persist: false }) entitiesUpdatedAt: Nullable<string>;

  @Attr({ persist: false }) health: AIVisibilityProjectHealth;

  @BelongsTo() instantInsightsReport: InstantInsightsReport;

  @Attr() instantInsightsReportId: number;

  // Read-only mirror of the linked report's name, exposed by the backend as a
  // plain attribute because the instantInsightsReport sideload is empty when an
  // admin views another org's project. Null when no report is linked.
  @Attr({ persist: false }) instantInsightsReportName: Nullable<string>;

  @Attr({ persist: false }) lastSyncedAt: string;

  @Attr({ persist: false }) lastUpdateAt: string;

  @Attr({ persist: false }) lastUpdateReason: string;

  @Attr({ persist: false }) lastUpdateStatus: string;

  @Attr() name: string;

  @BelongsTo() organisation: Organisation;

  @Attr() organisationId: number;

  @Attr({ persist: false }) raw: AIVisibilityProjectRaw;

  @Attr({ persist: false }) retrievalsPerRefresh: Nullable<number>;

  @Attr() selectedEntities: Nullable<AIVisibilityProjectSelectedEntities>;

  @Attr({ persist: false }) setupState: AIVisibilityProjectSetupState;

  @Attr() slug: string;

  @Attr() status: AIVisibilityProjectStatus;

  @Attr({ persist: false }) streamId: Nullable<number>;

  @Attr() trajaanProjectId: number;

  @Attr({ persist: false }) updatedAt: string;

  @Attr() workspaceSlug: string;
}

export default AIVisibilityProject;
